class SignaturePad extends HTMLElement {
	constructor() {
		super();
		this.attachShadow({ mode: 'open' });

		// Create the canvas element
		const canvas = document.createElement('canvas');
		this.canvas = canvas;

		canvas.style.border = '3px solid #982820';

		// minimum/placeholder values, they are completly overridden bellow.
		canvas.style.width = "100%";
		canvas.style.height = "200";

		this.shadowRoot.appendChild(canvas);

		const ctx = canvas.getContext('2d');
		this.ctx = ctx;

		ctx.lineCap = 'round';

		let drawing = false;


		// returns the line thickness scaled to the current canvas size 
		const getScaledLineWidth = () => {
			return (2 * canvas.width) / 1000;
		};

		const startDrawing = (e) => {
			drawing = true;
			ctx.beginPath();
			ctx.moveTo(e.offsetX, e.offsetY);
		};

		const draw = (e) => {
			if (!drawing) return;
			ctx.lineTo(e.offsetX, e.offsetY);
			ctx.stroke();
		};

		const stopDrawing = () => {
			drawing = false;
			ctx.closePath();
		};

		const getTouchPos = (canvas, touch) => {
			const rect = canvas.getBoundingClientRect();
			return {
				x: touch.clientX - rect.left,
				y: touch.clientY - rect.top
			};
		};

		const handleTouchStart = (e) => {
			e.preventDefault();
			const touch = e.touches[0];
			const pos = getTouchPos(canvas, touch);
			drawing = true;
			ctx.beginPath();
			ctx.moveTo(pos.x, pos.y);
		};

		const handleTouchMove = (e) => {
			e.preventDefault();
			if (!drawing) return;
			const touch = e.touches[0];
			const pos = getTouchPos(canvas, touch);
			ctx.lineTo(pos.x, pos.y);
			ctx.stroke();
		};

		const handleTouchEnd = (e) => {
			e.preventDefault();
			drawing = false;
			ctx.closePath();
		};


		this.adjustCanvasSize = function() {
			// We need to maintain an aspec ratio of H=12mm / L=64mm == 0.1875
			// Also, keep this a function defined here in the constructor, as we
			// need to capture `canvas` (this.canvas would not work, as `this` is
			// the window object at function call).
			const width = canvas.clientWidth;
			canvas.width = width;
			canvas.height = width * 0.1875;
			ctx.lineWidth = getScaledLineWidth();
		};

		// Event listeners for pc
		canvas.addEventListener('mousedown', startDrawing);
		canvas.addEventListener('mousemove', draw);
		canvas.addEventListener('mouseup', stopDrawing);
		canvas.addEventListener('mouseout', stopDrawing);

		// Event listeners for mobile
		canvas.addEventListener("touchstart", handleTouchStart, false);
		canvas.addEventListener("touchend", handleTouchEnd, false);
		canvas.addEventListener("touchmove", handleTouchMove, false);
	}

	connectedCallback() {
		// Adjust canvas size on load and resize. This maintains aspect
		// ratio so the signature fits our size requirements when exporting.
		window.addEventListener('resize', this.adjustCanvasSize);
		this.adjustCanvasSize();
	}

	disconnectedCallback() {
		// Clean up event listeners
		window.removeEventListener('resize', this.adjustCanvasSize);
	}

	clear() {
		this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
	}

	exportToPNG() {
		// Convert the canvas content to a data URL in PNG format
		const dataURL = this.canvas.toDataURL('image/png');
		// Convert the data URL to a binary Blob
		const blob = dataURLToBlob(dataURL);
		return blob;
	}

	hasMinimumPixels() {
		const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
		const data = imageData.data;
		let count = 0;

		/*
			On considère qu'une signature est valide si elle contient au moins 250 pixels non transparents.
			Chaque pixel est représenté par 4 valeurs consécutives dans le tableau data, correspondant
			aux composantes RGBA. On vérifie donc si la composante alpha (transparence) est non nulle.
			Ceci explique pourquoi on commence à l'indice 3 et on incrémente de 4 à chaque itération.
		*/
		for (let i = 3; i < data.length; i += 4) {
			if (data[i] !== 0) {
				count++;
				if (count >= 250) {
					return true;
				}
			}
		}
		return false;
	}
}

customElements.define('signature-pad', SignaturePad);


async function fetchPendingSheets(hub_api_url) {
	try {
		const url = `${hub_api_url}/intra/my-pending-sheets`;
		const response = await fetch(url, { credentials: 'include' });
		const sheets = await response.json();
		for (let sheet of sheets) {
			sheet.period.start = moment(sheet.period.start);
			sheet.period.end = moment(sheet.period.end);
			const noon = moment(sheet.period.start).hour(12).minute(0).second(0);
			if (sheet.period.type == "seminary") {
				const timeofday = sheet.period.start.isBefore(noon) ? "matin" : "après-midi";
				const date = sheet.period.start.format("DD/MM/YYYY");
				sheet.title = `Séminaire du ${date} - ${timeofday}`;
			} else {
				const start = sheet.period.start.format("DD/MM/YYYY");
				const end = sheet.period.end.format("DD/MM/YYYY");
				sheet.title = `e-learning - Run du ${start} au ${end}`;
			}
		}
		return sheets;
	} catch(err) {
		console.warn("failed to fetch pending attendance sheets for current user");
		return [];
	}
}


async function postSignature(hub_api_url, sheet_id, signature_blob) {
	// Create a FormData object and append the Blob as "signature_img"
	const formData = new FormData();
	formData.append('signature_img', signature_blob, 'signature.png');

	const url = `${hub_api_url}/attendance-sheets/${sheet_id}/sign`;
	// Post the form data to the API endpoint
	const response = await fetch(url, {
		method: 'POST',
		body: formData,
		credentials: 'include',
	});
	if (response.status != 201) {
		throw new Error(`Echec de la requête au serveur : code ${response.status}`);
	}
}


function dataURLToBlob(dataURL) {
	// Given a data url (as in: <img src="data:xxxx">), convert it to binary data.
	const byteString = atob(dataURL.split(',')[1]);
	const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
	const arrayBuffer = new ArrayBuffer(byteString.length);
	const uintArray = new Uint8Array(arrayBuffer);

	for (let i = 0; i < byteString.length; i++) {
		uintArray[i] = byteString.charCodeAt(i);
	}

	return new Blob([uintArray], { type: mimeString });
}


intra_module.directive('pendingSignaturesAlertbox', function() {
	return {
		restrict: 'E',
		templateUrl: 'directives/pending-signatures-alertbox/template.html',
		controller: 'PendingSignaturesAlertboxCtrl',
		replace: true,
	};
});

intra_module.controller('PendingSignaturesAlertboxCtrl', function($scope, $uibModal, hub_api_url) {
	$scope.sheets = [];

	async function _fetchPendingSheets() {
		$scope.sheets = await fetchPendingSheets(hub_api_url);
	}

	_fetchPendingSheets();
});


intra_module.controller('SheetSignatureCtrl', function($scope, sheet_id, hub_api_url, ngNotify, $location) {
	$scope.requesting = false;
	$scope.sheet = null;
	$scope.student = null;
	$scope.period = null;
	$scope.today = moment().format("dddd DD MMMM YYYY");

	async function fetchSheet() {
		const sheets = await fetchPendingSheets(hub_api_url);
		for (const sheet of sheets) {
			if (sheet.id == sheet_id) {
				$scope.sheet = sheet;
				$scope.student = sheet.student;
				$scope.period = sheet.period;
				$scope.$apply();
				return;
			}
		}
		console.error("No such pending sheet: ", sheet_id);
	}

	$scope.clear = function() {
		const pad = document.getElementById('signature-pad');
		if (pad !== null) {
			pad.clear();
		}
	};

	$scope.cancel = function() {
		$location.path("/");
	};

	$scope.confirm = async function() {
		const pad = document.getElementById('signature-pad');

		if (!pad.hasMinimumPixels()) {
			ngNotify.set('La zone de signature est insuffisamment remplie.', 'error');
			return;
		}

		const signature_blob = pad.exportToPNG();
		try {
			await postSignature(hub_api_url, sheet_id, signature_blob);
			ngNotify.set('Signature apposée.', 'success');
			$location.path("/");
		} catch (err) {
			ngNotify.set(`Une erreur est survenue pendant la signature: ${err}`, 'error');
		}
	};

	$scope.formatSeminarySchedule = function(timeslots) {
		const lines = [];
		for (let slot of timeslots) {
			const start = slot.start.slice(0, 5); // strip seconds
			const end = slot.end.slice(0, 5);
			const hours = Number(end.split(':')[0]) - Number(start.split(':')[0]);
			lines.push("");
			lines.push(`Module ${slot.module} — ${slot.title}`);
			lines.push(`De ${start} à ${end}, soit ${hours}H`);
		}
		return lines.join("\n");
	};

	fetchSheet();
});
